import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  Collapse,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Psychology as StrategyIcon,
  Lightbulb as GuidanceIcon,
  Refresh as RefreshIcon,
  LocalHospital as ConsultationIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import CareNotesManager from '../supervisor/CareNotesManager';
import therapistApi from '../../services/therapistApi';

function CareNotesPanel({ clientId, token }) {
  const [careNotes, setCareNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNoteForMenu, setSelectedNoteForMenu] = useState(null);
  const [initialDialogContent, setInitialDialogContent] = useState('');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');

  useEffect(() => {
    // Only load care notes if we have a valid clientId
    if (clientId) {
      loadCareNotes();
    }
    
    // Check if there's initial content from a message
    const initialContent = localStorage.getItem('careNoteInitialContent');
    if (initialContent) {
      setInitialDialogContent(initialContent);
      setShowCreateDialog(true);
      localStorage.removeItem('careNoteInitialContent');
    }
  }, [clientId, includeInactive]);

  const loadCareNotes = async () => {
    // Guard against undefined clientId
    if (!clientId) {
      console.warn('Cannot load care notes: clientId is undefined');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const response = await therapistApi.getClientCareNotes(clientId, includeInactive, token);
      setCareNotes(response.careNotes || []);
    } catch (err) {
      console.error('Error loading care notes:', err);
      setError(err.response?.data?.msg || 'Failed to load care notes');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCareNotes();
    setRefreshing(false);
  };

  const handleMenuOpen = (event, note) => {
    setAnchorEl(event.currentTarget);
    setSelectedNoteForMenu(note);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNoteForMenu(null);
  };

  const handleArchive = async (reason) => {
    if (!selectedNoteForMenu) return;
    
    try {
      await therapistApi.archiveCareNote(selectedNoteForMenu._id, reason, token);
      await loadCareNotes();
      handleMenuClose();
    } catch (err) {
      console.error('Error archiving care note:', err);
      setError(err.response?.data?.msg || 'Failed to archive care note');
    }
  };

  const handleReactivate = async (noteId) => {
    try {
      await therapistApi.reactivateCareNote(noteId, token);
      await loadCareNotes();
    } catch (err) {
      console.error('Error reactivating care note:', err);
      setError(err.response?.data?.msg || 'Failed to reactivate care note');
    }
  };

  const handleEdit = (note) => {
    setSelectedNote(note);
    setShowCreateDialog(true);
    handleMenuClose();
  };

  const getCareNoteIcon = (type) => {
    switch (type) {
      case 'warning':
        return <WarningIcon />;
      case 'strategy':
        return <StrategyIcon />;
      default:
        return <GuidanceIcon />;
    }
  };

  const getCareNoteColor = (type) => {
    switch (type) {
      case 'warning':
        return 'error';
      case 'strategy':
        return 'secondary';
      default:
        return 'primary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'success';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'consultation':
        return <ConsultationIcon fontSize="small" />;
      case 'therapist_dashboard':
        return <DashboardIcon fontSize="small" />;
      default:
        return null;
    }
  };

  // Group notes by status
  const activeNotes = careNotes.filter(note => note.active);
  const archivedNotes = careNotes.filter(note => !note.active);

  if (loading && !refreshing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  // Show message if no client is selected
  if (!clientId) {
    return (
      <Box>
        <Alert severity="info">
          Please select a client to view their care notes
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Care Notes
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Show Archived"
          />
          <IconButton onClick={handleRefresh} disabled={refreshing} aria-label="Refresh care notes">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedNote(null);
              setShowCreateDialog(true);
            }}
          >
            Add Care Note
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Active Notes */}
      {activeNotes.length === 0 && !includeInactive ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No active care notes for this client.
          </Typography>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
            sx={{ mt: 2 }}
          >
            Add First Care Note
          </Button>
        </Paper>
      ) : (
        <>
          {activeNotes.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Active Care Notes ({activeNotes.length})
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {activeNotes.map((note) => (
                  <Grid item xs={12} md={6} key={note._id}>
                    <Card>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            {getCareNoteIcon(note.type)}
                            <Chip
                              label={note.type.toUpperCase()}
                              size="small"
                              color={getCareNoteColor(note.type)}
                            />
                            <Chip
                              label={note.priority.toUpperCase()}
                              size="small"
                              color={getPriorityColor(note.priority)}
                              variant="outlined"
                            />
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, note)}
                            aria-label="More options"
                          >
                            <MoreIcon />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" paragraph>
                          {note.content}
                        </Typography>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" gap={1}>
                            <Chip
                              label={`AI: ${note.applicableToAI ? 'Yes' : 'No'}`}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Human: ${note.applicableToHuman ? 'Yes' : 'No'}`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          {note.source && (
                            <Tooltip title={`Source: ${note.source}`}>
                              {getSourceIcon(note.source)}
                            </Tooltip>
                          )}
                        </Box>
                        
                        {note.aiSuggestedVersion?.wasEdited && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            * Edited from AI suggestion
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Archived Notes */}
          {includeInactive && archivedNotes.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom color="text.secondary">
                Archived Care Notes ({archivedNotes.length})
              </Typography>
              <Grid container spacing={2}>
                {archivedNotes.map((note) => (
                  <Grid item xs={12} md={6} key={note._id}>
                    <Card sx={{ opacity: 0.7 }}>
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <ArchiveIcon color="disabled" />
                            {getCareNoteIcon(note.type)}
                            <Chip
                              label={note.type.toUpperCase()}
                              size="small"
                              disabled
                            />
                          </Box>
                          <Tooltip title="Reactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(note._id)}
                              aria-label="Reactivate care note"
                            >
                              <UnarchiveIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        <Typography variant="body2" paragraph color="text.secondary">
                          {note.content}
                        </Typography>
                        
                        {note.archiveReason && (
                          <Typography variant="caption" color="text.secondary">
                            Archived: {note.archiveReason}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEdit(selectedNoteForMenu)}>
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => {
          setArchiveDialogOpen(true);
          handleMenuClose();
        }}>
          <ArchiveIcon sx={{ mr: 1 }} /> Archive
        </MenuItem>
      </Menu>

      {/* Archive Reason Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => { setArchiveDialogOpen(false); setArchiveReason(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive Care Note</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for archiving this care note.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Archive Reason"
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
            placeholder="e.g., No longer relevant, superseded by new note, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setArchiveDialogOpen(false); setArchiveReason(''); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!archiveReason.trim()}
            onClick={() => {
              handleArchive(archiveReason.trim());
              setArchiveDialogOpen(false);
              setArchiveReason('');
            }}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <CareNotesManager
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedNote(null);
          setInitialDialogContent('');
        }}
        consultationId={null} // Not from consultation
        clientId={clientId}
        initialContent={selectedNote?.content || initialDialogContent}
        onCareNoteCreated={() => {
          loadCareNotes();
          setShowCreateDialog(false);
          setSelectedNote(null);
        }}
        token={token}
        // For editing, we'd need to enhance CareNotesManager to support updates
        editMode={!!selectedNote}
        noteId={selectedNote?._id}
        initialType={selectedNote?.type}
        initialPriority={selectedNote?.priority}
        initialApplicableToAI={selectedNote?.applicableToAI}
        initialApplicableToHuman={selectedNote?.applicableToHuman}
      />
    </Box>
  );
}

export default CareNotesPanel;